import React, { memo, useCallback } from 'react';

import Box from '@mui/material/Box';
import type { CheckboxProps } from '@mui/material/Checkbox';
import Checkbox from '@mui/material/Checkbox';
import { styled } from '@mui/material/styles';

import type { Sx } from '@/shared/types/uiTypes';

import { Z_INDEX } from '@/config/z-index';
import { normalizeSx } from '@/shared/lib/normalizeSx';

const CheckboxIcon = styled('span')(({ theme }) => ({
  borderRadius: 0,
  width: 14,
  height: 14,
  'input:hover ~ &': {
    ...theme.applyStyles('dark', {
      backgroundColor: 'theme.palette.gx.highlight'
    })
  },
  ...theme.applyStyles('dark', {
    border: `1px solid ${theme.palette.gx.textDisabled}`
  })
}));

const CheckedIcon = styled(CheckboxIcon)(({ theme }) => ({
  '&::before': {
    display: 'block',
    width: 14,
    height: 14,
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='-1 -1 14 14'%3E%3Cpath" +
      " fill-rule='evenodd' clip-rule='evenodd' d='M8.54167 3.0875L3.54167 8.0875L1.25 5.79583L1.8375 5.20833L3.54167 6.90833L7.95417 2.5L8.54167 3.0875Z " +
      "' fill='%23fff'/%3E%3C/svg%3E\")",
    content: '""'
  },
  ...theme.applyStyles('dark', {
    border: `1px solid ${theme.palette.gx.text}`
  })
}));

const checkboxSx: Sx = { '&:hover': { backgroundColor: 'transparent' } };

const lockedCheckboxSx: Sx = {
  ...checkboxSx,
  opacity: '1 !important'
};

const lockedWrapperSx: Sx = {
  position: 'relative',
  display: 'inline-flex'
};

const modeLockOverlaySx: Sx = {
  position: 'absolute',
  inset: 0,
  cursor: 'not-allowed',
  zIndex: Z_INDEX.raised
};

const modeLockIconSx: Sx = {
  display: 'block',
  width: 14,
  height: 14,
  backgroundColor: 'gx.textDisabled'
};

const modeLockIcon = <Box sx={modeLockIconSx} />;

export interface CustomCheckboxProps extends CheckboxProps {
  /** Shows a disabled lock-style square while preserving the selected state visually. */
  modeLocked?: boolean;
  /** Optional callback for clicks on the lock overlay when modeLocked is true. */
  onLockedClick?: () => void;
}

const CustomCheckbox = ({
  onClick,
  onMouseDown,
  modeLocked,
  onLockedClick,
  sx,
  ...props
}: CustomCheckboxProps) => {
  const handleMouseDown = useCallback<NonNullable<CheckboxProps['onMouseDown']>>(
    e => {
      e.stopPropagation();
      onMouseDown?.(e);
    },
    [onMouseDown]
  );

  const handleClick = useCallback<NonNullable<CheckboxProps['onClick']>>(
    e => {
      e.stopPropagation();
      onClick?.(e);
    },
    [onClick]
  );

  const checkbox = (
    <Checkbox
      disableRipple
      checkedIcon={<CheckedIcon />}
      icon={<CheckboxIcon />}
      size='small'
      sx={[modeLocked ? lockedCheckboxSx : checkboxSx, ...normalizeSx(sx)]}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      {...props}
      {...(modeLocked && {
        disabled: true,
        icon: modeLockIcon,
        checkedIcon: modeLockIcon,
        indeterminateIcon: modeLockIcon
      })}
    />
  );

  if (!modeLocked) return checkbox;

  return (
    <Box sx={lockedWrapperSx}>
      {checkbox}
      <Box
        data-testid='mode-lock-overlay'
        sx={modeLockOverlaySx}
        onClick={e => {
          e.stopPropagation();
          onLockedClick?.();
        }}
      />
    </Box>
  );
};

export default memo(CustomCheckbox);
